/****************************************************
 * Module Name:  RevertPrice.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import { Box, Drawer, Typography, Button, Tooltip, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import CommonSnackbar from "../common/commonSnackBar";
import Constants from "../../utility/constants";
import StockSearchCriteria from "./stockSearchCriteria";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DataGridPro, GridFilterInputValue, getGridStringOperators, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import Utility from "../../utility/utility";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import CommonStoneSelection from "../common/commonStoneSelection";
import VideoLibraryTwoToneIcon from '@mui/icons-material/VideoLibraryTwoTone';
import renderCellExpand from "../common/dataGridCellExpand";
import StoneMediaModal from "../common/stoneMediaModal";
import StoneDetailModal from "../common/stoneDetailModal";
import TuneIcon from '@mui/icons-material/Tune';
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LoadingButton } from "@mui/lab";
import ReplyAllIcon from '@mui/icons-material/ReplyAll';
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import NivodaProxy from "../../utility/proxy/manager/nivodaProxy";
import NoPermission from "../common/noPermission";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PacketHistoryModal from "../common/packetHistoryModal";
import PageTitle from "../common/pageTitle";

const RevertPrice = () => {

    const [stockCriteriaState, setStockCriteriaState] = useState('');
    const [RevertSuccessSnackbar, setRevertSuccessSnackbar] = useState(false);
    const [searchCriteria, setSearchCriteria] = useState("");
    const [stoneListLoader, setStoneListLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [drawerExpanded, setDrawerExpanded] = useState(true);
    const [stoneList, setStoneList] = useState([]);
    const [date, setDate] = useState(new Date());
    const [selectionModel, setSelectionModel] = useState([]);
    const [showStoneMediaModal, setShowStoneMediaModal] = useState(false);
    const [stoneId, setStoneId] = useState("");
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [kapanId, setKapanId] = useState("");
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
    const [columnNames, setColumnNames] = React.useState([]);
    const [stoneMediaURL, setStoneMediaURL] = useState([]);
    const [revertLoader, setRevertLoader] = useState(false);
    const [disableRevert, setDisableRevert] = useState(true);
    const [isColumnsHidden, setIsColumnsHidden] = useState(true);
    const [access, setAccess] = useState(false);

    useEffect(() => {
        PageTitle("OPERATION_REVERT_PRICE");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.OPERATION_REVERT_PRICE
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        }
    }

    const fetchLiveStoneData = (data, stockCriteriaStored) => {
        setSearchCriteria(data);
        setStockCriteriaState(stockCriteriaStored);
        setStoneListLoader(true);

        data.status = [];
        data.status.push('AVAILABLE');
        data.status.push('HOLD');
        data.status.push('MEMO_OUT');

        StoneProxy.GetFilteredStock(
            data, (responseData) => {
                if (responseData) {
                    const updatedStoneList = responseData.map((record, idx) => {
                        return { ...record, id: idx };
                    });
                    const distinctStatus = Utility.getDistinctOptions(responseData, 'status');
                    prepareColumns(distinctStatus);
                    setIsColumnsHidden(true);
                    setStoneList(updatedStoneList);
                    setStoneListLoader(false);
                } else {
                    setErrorSnackbar(true);
                    setStoneListLoader(false);
                }
            }
        );
    };

    const handleSearch = (stoneList) => {
        setStoneListLoader(true);
        let stoneIds = null;
        if (selectionModel.length > 0) {
            stoneIds = selectionModel;
        } else {
            stoneIds = stoneList.map(item => item.stoneid);
        }

        let reqData = {
            stoneids: stoneIds,
            from: (date !== null && date !== "") ?
                date.toISOString() : null,
        }

        NivodaProxy.GetPricePreview(
            reqData,
            (Response) => {
                if (Response) {
                    const updatedRows = stoneList.map(stone => {
                        const record = Response.find(priceData => priceData.stoneid === stone.stoneid);
                        return {
                            ...stone,
                            newprice: record ? record.newprice : stone.price,
                        };
                    });
                    setStoneList(updatedRows);
                    setIsColumnsHidden(false);
                    setStoneListLoader(false);
                }
                else {
                    setErrorSnackbar(true);
                    setStoneListLoader(false);
                }
            }
        )
    }

    const handleClose = () => {
        setShowStoneDetailModal(false);
        setRevertSuccessSnackbar(false);
    };

    const handleOnRevert = () => {
        setRevertLoader(true);
        const filteredStoneIds = selectionModel.filter(stoneid => {
            const stoneData = stoneList.find(row => row.stoneid === stoneid);
            return stoneData && stoneData.price !== stoneData.newprice;
        });
        let reqData = {
            stoneids: filteredStoneIds,
            from: (date !== null && date !== "") ?
                date.toISOString() : null,
        }

        NivodaProxy.RevertPrice(
            reqData,
            (Response) => {
                if (Response) {
                    setSelectionModel([]);
                    setRevertSuccessSnackbar(true);
                    fetchLiveStoneData(searchCriteria, stockCriteriaState)
                    setRevertLoader(false);
                }
                else {
                    setErrorSnackbar(true);
                    setStoneListLoader(false);
                    setRevertLoader(false);
                }
            }
        )
    }

    const handleSelection = (data) => {
        const [tempStockData, tempSelectionModel] = Utility.handleSelection(data, stoneList, selectionModel);

        setStoneList(tempStockData);
        setSelectionModel(tempSelectionModel);
    }

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const customFilterOperators = [
        {
            label: '=',
            value: 'equals',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value === Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
        {
            label: '!=',
            value: 'notEquals',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value !== Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
        {
            label: '>=',
            value: 'greaterThanOrEqual',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value >= Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
        {
            label: '>',
            value: 'greaterThan',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value > Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
        {
            label: '<=',
            value: 'lessThanOrEqual',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value <= Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
        {
            label: '<',
            value: 'lessThan',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value < Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
    ];

    const defaultFilters = () => {
        const stringOperators = getGridStringOperators().filter(operator =>
            ['isEmpty', 'isNotEmpty', 'isAnyOf'].includes(operator.value)
        );
        const combinedFilterOperators = [hasColorFilterOperator, ...customFilterOperators, ...stringOperators];
        return combinedFilterOperators;
    }

    const hasColorFilterOperator = {
        label: 'has color',
        value: 'hasColor',
        getApplyFilterFn: (filterItem, column) => {
            if (!filterItem.value) {
                return null;
            }
            //keep this
            // return (params) => {
            //     const cellClassName = params.row[params.field];
            //     console.log(cellClassName);
            //     // return cellClassName.includes(filterItem.value);
            // };
            return ({ value, row, column, apiRef }) => {
                const { newprice, price } = row;
                let cellClassName = '';
                if (newprice === null || price === null || newprice === '' || price === '' || newprice === undefined || price === undefined) {
                    cellClassName = '';
                }
                else if (newprice !== price) cellClassName = 'greenCell';
                else cellClassName = 'lavenderCell';
                return cellClassName.startsWith(filterItem.value.toLowerCase());
            };
        },
        InputComponent: ({ item, applyValue }) => (
            <FormControl fullWidth sx={{ marginLeft: 1 }}>
                <InputLabel>Value</InputLabel>
                <Select
                    value={item.value || ''}
                    onChange={(event) => {
                        const newValue = event.target.value || null;
                        applyValue({ ...item, value: newValue });
                    }}
                    label="Value"
                    sx={{ height: "49px", width: '11rem' }}
                >
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="green">Green</MenuItem>
                    <MenuItem value="lavender">Lavender</MenuItem>
                </Select>
            </FormControl>
        ),
        InputComponentProps: {
            type: 'singleSelect',
            valueOptions: ['green', 'lavender'],
        },
    };

    const prepareColumns = (distinctStatus) => {
        let columnNames = [];

        Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
            if (columnsData.hasOwnProperty(iColPermission)) {
                const columnData = columnsData[iColPermission];
                if (columnData.field === "status") {
                    columnData.type = "singleSelect";
                    columnData.valueOptions = distinctStatus;
                    columnData.filterOperators = Utility.combinedFilters();
                }
                columnNames.push(columnData);
                if (columnData.field === "stoneid") {
                    columnNames.push({
                        field: "mumid",
                        headerName: "ID",
                        headerAlign: "center",
                        align: "center",
                        width: 90,
                        renderCell: (params) => (!params.value) ? "" : params.value,
                    })
                }
                if (columnData.field === "price") {
                    columnNames.push({
                        field: "newprice",
                        headerName: "NEW PPC",
                        headerAlign: "center",
                        align: "right",
                        width: 80,
                        hideable: false,
                        filterOperators: defaultFilters(),
                        renderCell: (params) => (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ marginRight: '10px' }}>{Utility.ConvertNumberWithSpecifiedDigits(params.row.newprice)}</span>
                            </div>
                        ),
                        cellClassName: (params) => {
                            return params.row.newprice !== params.row.price ? 'greenCell' : 'lavenderCell';
                        }
                    })
                }
            }
        });
        setColumnNames(columnNames);
    }

    const columnsData = {
        "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
            field: "stoneid",
            headerName: "STONEID",
            pinning: { position: 'left' },
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 95,
            cellClassName: 'stickyColumn',
            renderCell: (params) => (Utility.NeedToShowStoneDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "9dfe5dd6-726c-465b-9f61-22fca6660b4f": {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        "8a492e00-6167-45de-b078-e572e47c8a39": {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid)
                        setPacketId(params.row.packetid)
                        setShowPacketDetailModal(true)
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand
        },
        "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => Constants.STONE_TYPE[params.value],
            renderCell: (params) => Constants.STONE_TYPE[params.value],
        },
        "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.shape.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            width: 40,
            type: "number",
            valueFormatter: (params) => params.value,
            align: "right",
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.color.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 65,
            renderCell: renderCellExpand,
        },
        "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.clarity.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 75,
            renderCell: renderCellExpand,
        },
        "3597803f-8932-40c6-8de6-81006f4d6d5b": {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.cut.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 50,
            renderCell: renderCellExpand,
        },
        "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.pol.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 40,
            renderCell: renderCellExpand,
        },
        "5541c182-21b8-496e-b342-b0b89f3d302f": {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.sym.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 40,
            renderCell: renderCellExpand,
        },
        "8296e627-0be5-4a66-830f-ea6867cea137": {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.flo.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 60,
            renderCell: renderCellExpand,
        },
        "798a8663-0f0c-41be-8512-16dd60d5f25e": {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 50,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount)
        },
        "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
            field: "rapnetprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.rapnetprice)
        },
        "39c634f1-2644-4307-acbb-983b4de4229e": {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        "13d48508-b8c9-4661-8a6c-9fb22a3408a3": {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 140,
            renderCell: renderCellExpand,
        },
        "567202e6-c31c-4c19-9998-1cacae05af6e": {
            field: "depth",
            headerName: "DEPTH",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: renderCellExpand,
        },
        "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.lab.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 40,
            renderCell: renderCellExpand,
        },
        "19ce7030-402c-495c-84ac-a29093bdb0ac": {
            field: "locationid",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 90,
            renderCell: renderCellExpand,
        },
        "ab3e506e-bddb-456d-b967-922dcee74d56": {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.location.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 100,
            renderCell: renderCellExpand,
        },
        "f97a0118-3891-4f77-abf7-8636c26d594c": {
            field: "country",
            headerName: "COUNTRY",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "cb7e6add-67ba-4194-ac49-fd0d967db4e8": {
            field: "quality",
            headerName: "QUALITY",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        "b718f242-4a82-42e7-b98a-40bbf0f67c21": {
            field: "shade",
            headerName: "SHADE",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: renderCellExpand,
        },
        "01e90aca-bdcb-4e8e-80ec-2d7007df8cd3": {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            type: "dateTime",
            width: 130,
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        "3c62ef48-9538-4df7-9484-a9ae1ba31086": {
            field: "height",
            headerName: "HEIGHT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: renderCellExpand,
        },
        "eed9b7eb-17e4-4ec6-9c9d-c299f885544c": {
            field: "width",
            headerName: "WIDTH",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 70,
            renderCell: renderCellExpand,
        },
        "8eaa8a03-d163-436d-8912-51ea06306f44": {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 100,
            renderCell: renderCellExpand,
        },
        "77e48fa7-d118-4ab8-865b-9b85b32a004f": {
            field: "fancycolor1",
            headerName: "F.COLOR",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "ccba9873-174e-4060-963a-9893c3625bb2": {
            field: "fancycolorovertone",
            headerName: "OVERTONE",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "c9aa6ee7-30e3-423e-a534-80bb5ef7bbaf": {
            field: "fancycolorintensity",
            headerName: "F.COL.INTENSITY",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        "706a5cfb-e196-43d1-bbe3-c52aad9a46c1": {
            field: "comment",
            headerName: "COMMENT",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "e7210f4a-6e0a-4591-8428-e6845004ed04": {
            field: "crownheight",
            headerName: "CR.HEIGHT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "c4e65984-1682-4c1e-812b-8938e9078b2d": {
            field: "crownangle",
            headerName: "CR.ANG",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "0f1de38d-3ae3-4209-a4d5-afe9f3018f22": {
            field: "culetsize",
            headerName: "CULET SZ",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "e3f77521-2c57-46bc-a02b-3c884685d2be": {
            field: "culetcondition",
            headerName: "CULET",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "e5c1f34e-6e1c-4ee8-bb8e-3236716fd1ff": {
            field: "depthpercent",
            headerName: "DEPTH%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 75,
            renderCell: renderCellExpand,
        },
        "e83c2978-9088-49f0-9dc1-bcced73025fd": {
            field: "gridlemin",
            headerName: "GIRDLEMIN",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "cee57bac-9c19-40d5-b51a-51ed913dbf73": {
            field: "intensitylongtitle",
            headerName: "INTENSITYLONGTITLE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "06d2a589-0d42-4dde-8724-acf2448c839a": {
            field: "laserinscription",
            headerName: "INS",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "dd934505-1865-48cb-878f-f5d3c18af2d5": {
            field: "note",
            headerName: "NOTE",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "61f8a5f2-8516-419a-b6fd-39ef109cc2f4": {
            field: "paviliondepth",
            headerName: "PV.DEPTH",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "70498a64-1f5f-4f03-8910-a6d7851e4aba": {
            field: "tablepercent",
            headerName: "TABLE%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 75,
            renderCell: renderCellExpand,
        },
        "db078198-4416-4d53-b3d0-70d3a5418f65": {
            field: "open",
            headerName: "OPEN",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "e9028170-e978-4978-a2c9-1889c1821495": {
            field: "gridlecondition",
            headerName: "GIRDLE COND",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "fe8f55f3-358a-4418-bcdf-c3da4b01db67": {
            field: "gridlepercent",
            headerName: "GIRDLE%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "daf69bba-bb1f-4d54-9dc7-9383ad429585": {
            field: "pavilionangle",
            headerName: "PV.ANGLE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "ecfdfc0c-6a25-4a6d-9c8f-3406c23410af": {
            field: "white",
            headerName: "WHITE",
            headerAlign: "center",
            align: "center",
            renderCell: renderCellExpand,
        },
        "24ba0bf8-4ca1-4726-ad90-19a62c8c72e6": {
            field: "boxid",
            headerName: "BOXID",
            headerAlign: "center",
            width: 60,
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "cf694b75-ba5d-4a73-8fcf-e7566974c4b6": {
            field: "media",
            headerName: "MEDIA",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: (params) => (
                <Tooltip title={"Stone Media"} sx={{ cursor: "pointer" }}>
                    <VideoLibraryTwoToneIcon
                        sx={{ cursor: "pointer" }}
                        onClick={() => {
                            setStoneMediaURL(params.row.stonemediaurl);
                            setShowStoneMediaModal(true);
                        }} />
                </Tooltip>
            ),
        },
        "f3223657-c8d2-449c-b2f5-5a54c78401ce": {
            field: "tableinc",
            headerName: "TABLE INC",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 120,
        },
        "f114ad2c-8560-4da7-9b03-03ba6bfe39a5": {
            field: "sideinc",
            headerName: "SIDE INC",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "e993a4f7-7c50-4d83-abae-fccc03382742": {
            field: "h_and_a",
            headerName: "H&A",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "f2fe7666-6489-40ce-b4b7-040c68c88bf3": {
            field: "eyeclean",
            headerName: "EYE CLEAN",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "b38b9fdf-bfb8-43c6-82a2-1e366fa71523": {
            field: "tinge",
            headerName: "TINGE",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.tinge.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 120,
        },
        "c95089ee-166d-4783-817c-2638f6d40df1": {
            field: "milky",
            headerName: "MILKY",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.milky.sort((a, b) => a.seq - b.seq).map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 120,
        },
        "2a6475ff-481f-48c6-9e92-3ede0b97d7c0": {
            field: "luster",
            headerName: "LUSTER",
            headerAlign: "center",
            align: "center",
            width: 120,
        },
        "556ae429-3605-4df4-88d8-b5223fc2e2fb": {
            field: "tableblack",
            headerName: "TABLE BLACK",
            headerAlign: "center",
            align: "center",
            type: "boolean",
            width: 120,
        },
        "5c70d1ca-2a7b-4660-8160-0beff7ad1150": {
            field: "sideblack",
            headerName: "SIDE BLACK",
            headerAlign: "center",
            align: "center",
            type: "boolean",
            width: 120,
        },
        "e32fe363-07cc-4307-82d2-82ae1069b5be": {
            field: "age",
            headerName: "AGE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => {
                if (params.value)
                    return Utility.ConvertDateToAge(params.value);
                else
                    return null;
            },
            renderCell: (params) => {
                return params.value;
            },
        }
    };

    // const columns = columnNames;
    function CustomToolbar() {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <CommonStoneSelection
                    title={"Enter multiple Stone Ids"}
                    handleSelection={handleSelection}
                    buttonDisabled={stoneList.length === 0}
                />
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
                {searchCriteria === "" ? (
                    <Box />
                ) : stoneListLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => fetchLiveStoneData(searchCriteria, stockCriteriaState)}
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <Tooltip title="Revert Price">
                    <LoadingButton
                        variant="outlined"
                        size="small"
                        component="label"
                        sx={{ ml: "5px" }}
                        loading={revertLoader}
                        loadingPosition="start"
                        startIcon={<ReplyAllIcon size="large" />}
                        disabled={disableRevert}
                        onClick={handleOnRevert}
                    >
                        Revert
                    </LoadingButton>
                </Tooltip>
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }
    const columns = isColumnsHidden
        ? columnNames.filter((col) => col.field !== "newprice")
        : columnNames;

    return (
        <Box>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {RevertSuccessSnackbar ? (
                <CommonSnackbar
                    show={RevertSuccessSnackbar}
                    message={`Price Reverted Successfully`}
                    variant={"success"}
                    autoHide={true}
                    title={"success"}
                    handleClose={handleClose}
                />
            ) : null}
            <Drawer
                anchor="left"
                open={drawerExpanded}
                style={{ width: "50%" }}
                onClose={() => setDrawerExpanded(!drawerExpanded)}>
                <StockSearchCriteria
                    displaySearchCriteria={false}
                    stockCriteriaStored={stockCriteriaState}
                    handleSearch={fetchLiveStoneData}
                    showSaveSearchAs={false}
                    setCustomerId={(data) => { }}
                    drawerClose={() => setDrawerExpanded(false)}
                    setFilters={(stockCriteriaStored) => setStockCriteriaState(stockCriteriaStored)}
                    readOnly={false}
                    enableReportNoSearchBox={false}
                />
            </Drawer>

            {showStoneDetailModal ? (
                <StoneDetailModal
                    show={showStoneDetailModal}
                    stoneId={stoneId}
                    handleClose={handleClose}
                />
            ) : null}

            {showKapanDetailModal ? (
                <KapanHistoryModal
                    show={showKapanDetailModal}
                    kapanId={kapanId}
                    close={() => setShowKapanDetailModal(false)}
                />
            ) : null}

            {showPacketDetailModal ? (
                <PacketHistoryModal
                    show={showPacketDetailModal}
                    packetId={packetId}
                    kapanId={kapanId}
                    close={() => setShowPacketDetailModal(false)}
                />
            ) : null}
            {showStoneMediaModal ? (
                <>
                    <StoneMediaModal
                        show={showStoneMediaModal}
                        stoneMediaURL={stoneMediaURL}
                        handleClose={() => setShowStoneMediaModal(false)}
                    />
                </>
            ) : null}
            {
                access ? <NoPermission /> : (
                    <Box px={1}>
                        <Box
                            m="16px"
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "20px",
                                width: "100%",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                            }}
                        >
                            {/* All Filters Button */}
                            <Tooltip title="Show All Filters">
                                <Button
                                    variant="contained"
                                    size="small"
                                    color="inherit"
                                    sx={{ ml: "5px", backgroundColor: '#454545', width: "128px", height: "50px", color: 'white', '&:hover': { backgroundColor: '#454545' } }}
                                    startIcon={<TuneIcon size="large" />}
                                    onClick={() => setDrawerExpanded(true)}
                                >
                                    All Filters
                                </Button>
                            </Tooltip>

                            {/* Date + Search Container */}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "15px",
                                    flex: 1,
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                }}
                            >
                                {/* Date Picker */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        width: { xs: "100%", sm: "350px" },
                                        gap: "10px",
                                    }}
                                >
                                    <Typography mt={1}>Date</Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DateTimePicker
                                            ampm={true}
                                            renderInput={(props) => (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    {...props}
                                                    inputProps={{ ...props.inputProps, readOnly: true }}
                                                    sx={{ height: "2.45rem" }}
                                                />
                                            )}
                                            value={date}
                                            inputFormat="DD/MM/YYYY hh:mm:ss a"
                                            onChange={(newValue) => {
                                                const toDate = dayjs(newValue.$d);
                                                setDate(toDate);
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Box>

                                {/* Search Button */}
                                <Box
                                    sx={{
                                        "& .Mui-disabled": {
                                            backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                            color: "rgba(0, 0, 0, 0.26) !important"
                                        },
                                        width: { xs: "100%", sm: "auto" },
                                        marginTop: { xs: 0, sm: "3px" },
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => handleSearch(stoneList)}
                                        startIcon={<SearchOutlinedIcon />}
                                        sx={{
                                            width: { xs: "100%", sm: "auto" },
                                            height: "40px",
                                        }}
                                    >
                                        Search
                                    </Button>
                                </Box>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .lavenderCell": {
                                    color: "#080808",
                                    backgroundColor: "#D8BFD8",
                                },
                                "& .greenCell": {
                                    color: "#080808",
                                    backgroundColor: "#B6D7A8"
                                },
                                "& .redCell": {
                                    color: "#080808",
                                    backgroundColor: "#f5d7d7",
                                },
                                "& .yellowCell": {
                                    color: "#080808",
                                    backgroundColor: "#F1E68C",
                                },
                                height: "76vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={false}
                                density="compact"
                                loading={stoneListLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                slots={{ toolbar: CustomToolbar }}
                                checkboxSelection={true}
                                rowSelectionModel={selectionModel}
                                onRowSelectionModelChange={(newSelectionModel) => {
                                    setSelectionModel(newSelectionModel);
                                    setDisableRevert(newSelectionModel.length > 0 ? false : true);
                                }}
                                selectionModel={selectionModel}
                                rows={stoneList}
                                columns={columns}
                                getRowId={(row) => row.stoneid}
                                rowSpacingType="border"
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns status and traderName, the other columns will remain visible
                                            id: false,
                                            locationid: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )
            }
        </Box>
    )
}

export default RevertPrice;